import { AddCircle } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DeleteButton, SaveButton } from '~/components/button';
import { AddPropertyGroupDialog } from '~/components/dialogs/add-property-group';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { FormLayout } from '~/components/settings';
import { useAppContext } from '~/contexts';
import { FormHeading } from '~/pages/devices/show/lib';
import { AlertMessage } from '../../components/alert-message';
import type { ShowSettings__Show as Show } from '../ShowSettings.generated';
import { useSettingsForm } from '../lib/settings';
import { ActionBar } from '../lib/styles';
import type { ShowDetail__PropertyGroup as PropertyGroup } from '../queries.generated';

export type ShowSettingsProps = {
  propertyGroups: ReadonlyArray<PropertyGroup>;
  show: Show;
  showTags?: string[];
  networkDefaultImageDuration: number;
};

export const SettingsForm = (showSettings: ShowSettingsProps) => {
  const { t } = useTranslation();
  const { show } = showSettings;
  const { currentNetwork } = useAppContext();
  const canManagePropertyGroups = currentNetwork.canManagePropertyGroups.value;

  const {
    addPropertyGroup,
    confirmDialogProps,
    deletePropertyGroup,
    fanconnectSettings,
    filteredPropertyGroups,
    formik,
    generalSettings,
    propertyGroupSettings,
    setShowAddPropertyDialog,
    showAddPropertyDialog,
    viewSettings,
  } = useSettingsForm(showSettings);

  return (
    <>
      <AlertMessage show={show} />
      <form onSubmit={formik.handleSubmit}>
        <ActionBar>
          {canManagePropertyGroups && (
            <Button
              disabled={!filteredPropertyGroups.length}
              onClick={() => setShowAddPropertyDialog(true)}
              startIcon={<AddCircle />}
              variant="contained"
            >
              {t('pages.shows.detail.settings.add_property_group_button')}
            </Button>
          )}
          <SaveButton
            disabled={
              !show.canUpdate.value || !formik.isValid || formik.isSubmitting || !formik.dirty
            }
            type="submit"
          >
            {t('common.dialogs.save')}
          </SaveButton>
        </ActionBar>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <FormLayout formHeading={t('common.labels.general')} formData={generalSettings} />

          {currentNetwork.canManageViews.value && (
            <FormLayout
              formHeading={t('pages.shows.detail.settings.view_settings_heading')}
              formData={viewSettings}
            />
          )}

          {formik.values.fanconnect && (
            <FormLayout
              formHeading={
                <FormHeading>
                  <Box>{t('pages.shows.detail.settings.fanconnect_heading')}</Box>
                  <DeleteButton
                    onClick={() => formik.setFieldValue('fanconnect', null, true)}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {t('common.dialogs.remove')}
                  </DeleteButton>
                </FormHeading>
              }
              formData={fanconnectSettings}
            />
          )}

          {propertyGroupSettings.map((settings) => (
            <FormLayout
              formHeading={
                <FormHeading>
                  <Box>{settings.name}</Box>
                  {canManagePropertyGroups && (
                    <DeleteButton
                      onClick={() =>
                        deletePropertyGroup({ propertyGroupId: settings.propertyGroupId })
                      }
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {t('common.dialogs.remove')}
                    </DeleteButton>
                  )}
                </FormHeading>
              }
              formData={settings.fields}
              key={settings.id}
            />
          ))}
        </Box>
      </form>
      <ConfirmDialog
        {...confirmDialogProps}
        title={t('pages.shows.detail.settings.delete_property_group_title')}
        confirm={t('pages.shows.detail.settings.delete_property_group_confirm')}
        prompt={t('pages.shows.detail.settings.delete_property_group_prompt')}
        deleteConfirm
      />
      <AddPropertyGroupDialog
        description={t('pages.shows.detail.settings.add_property_group_description')}
        onSubmit={addPropertyGroup}
        open={showAddPropertyDialog}
        onClose={() => {
          setShowAddPropertyDialog(false);
        }}
        propertyGroups={filteredPropertyGroups}
      />
    </>
  );
};
